const API_KEY = process.env.REACT_APP_AWS_FRIENDZ_API_KEY;
const API_BASE_URL = process.env.REACT_APP_BASE_URL;

export const subscriptionTiers = {
  FREE: {
    name: 'Free',
    price: 0,
    propertyAnalyses: 5,
    chatMessages: 20,
    features: ['Basic ROI calculations', 'Limited property data', 'Property comparison']
  },
  PREMIUM: {
    name: 'Premium',
    price: 29,
    propertyAnalyses: 50,
    chatMessages: 500,
    features: ['Advanced ROI metrics', 'Property comparison', 'Market trends', 'Portfolio tracking', 'Email support', 'PDF export']
  },
  UNLIMITED: {
    name: 'Unlimited',
    price: 99,
    propertyAnalyses: -1, // unlimited
    chatMessages: -1, // unlimited
    features: ['Everything in Premium', 'Priority support', 'API access', 'Custom integrations', 'Advanced analytics']
  }
};

export const subscriptionService = {
  // Get user's current subscription from existing backend
  async getCurrentSubscription(userId) {
    try {
      console.log('Fetching subscription for userId:', userId);
      console.log('API URL:', `${API_BASE_URL}/property_roi?userId=${userId}`);
      
      // Call the existing property_roi endpoint with GET method to fetch user data
      const response = await fetch(`${API_BASE_URL}/property_roi?userId=${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY
        }
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const userData = await response.json();
      console.log('User data from API:', userData);
      
      // Map backend subscription status to frontend tier
      const subscriptionStatusMap = {
        'free': 'FREE',
        'premium': 'PREMIUM', 
        'unlimited': 'UNLIMITED'
      };

      const tier = subscriptionStatusMap[userData.subscriptionStatus] || 'FREE';
      console.log('Mapped tier:', tier);
      
      return {
        tier: tier,
        status: 'active',
        userId: userId
      };
    } catch (error) {
      console.error('Error fetching subscription:', error);
      return { tier: 'FREE', status: 'active', userId: userId };
    }
  },

  // Get usage statistics from existing backend
  async getUsage(userId) {
    try {
      // Use the same GET endpoint to get user data including usage
      const response = await fetch(`${API_BASE_URL}/property_roi?userId=${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const userData = await response.json();
      
      // Map backend usage to frontend format
      return {
        propertyAnalyses: userData.monthlySearchCount || 0,
        chatMessages: userData.chatMessagesUsed || 0
      };
    } catch (error) {
      console.error('Error fetching usage:', error);
      return { propertyAnalyses: 0, chatMessages: 0 };
    }
  },

  // Create Stripe checkout session (mock for now)
  async createCheckoutSession(tier, userId) {
    try {
      // For now, simulate a successful checkout
      // Replace this with real Stripe integration later
      return {
        url: `https://checkout.stripe.com/pay/mock?tier=${tier}&userId=${userId}`
      };
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  },

  // Create customer portal session (mock for now)
  async createPortalSession(userId) {
    try {
      // For now, simulate a portal session
      // Replace this with real Stripe integration later
      return {
        url: `https://billing.stripe.com/mock/portal?userId=${userId}`
      };
    } catch (error) {
      console.error('Error creating portal session:', error);
      throw error;
    }
  },

  // Check if user can perform an action
  canPerformAction(subscription, usage, action) {
    const tier = subscriptionTiers[subscription.tier] || subscriptionTiers.FREE;
    
    switch (action) {
      case 'property_analysis':
        return tier.propertyAnalyses === -1 || usage.propertyAnalyses < tier.propertyAnalyses;
      case 'chat_message':
        return tier.chatMessages === -1 || usage.chatMessages < tier.chatMessages;
      default:
        return false;
    }
  },

  // Get usage percentage for display
  getUsagePercentage(subscription, usage, action) {
    const tier = subscriptionTiers[subscription.tier] || subscriptionTiers.FREE;
    
    switch (action) {
      case 'property_analysis':
        if (tier.propertyAnalyses === -1) return 0; // unlimited
        return Math.min((usage.propertyAnalyses / tier.propertyAnalyses) * 100, 100);
      case 'chat_message':
        if (tier.chatMessages === -1) return 0; // unlimited
        return Math.min((usage.chatMessages / tier.chatMessages) * 100, 100);
      default:
        return 100;
    }
  }
};